import React from "react";
import StatusCell from "./statusCell";
import { dateFormatter } from "components/helpers";
import { getFormattedDate, getFormattedDateTime } from "utils/helper";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import NotesCell from "./notesCell";
import ErrorCell from "./errorCell";

import AssignUserCell from "./assignUserCell";
import ErrorDetailsPreview from "./errorDetailsPreview";
import dayjs from "dayjs";

export const getStatusColor = (status) => {
  switch (status) {
    case "In-progress":
      return "#FFA500"; // Orange
    case "Practice Clarification":
      return "#1976d2"; // Blue
    case "Completed":
      return "#4caf50"; // Green
    case "Outreach Needed":
      return "#d32f2f"; // Red
    default:
      return "inherit";
  }
};

export const BULK_FIELDS = [
  {
    value: "adv_chart_status",
    label: "Advanced Charting Status",
    type: "status",
  },
  { value: "scribing_status", label: "Scribing Status", type: "status" },
  { value: "coding_status", label: "Coding Status", type: "status" },
  {
    value: "resource_id_adv_chart",
    label: "Advanced Charting Agent",
    type: "agent",
  },
  { value: "resource_id_scribing", label: "Scribing Agent", type: "agent" },
  { value: "resource_id_coding", label: "Coding Agent", type: "agent" },
];

export const COMPLETION_DATE_FIELD_MAP = {
  adv_chart_status: "adv_chart_compl_date",
  scribing_status: "scribing_compl_date",
  coding_status: "coding_compl_date",
};

export const getStatusOptions = (field) => {
  return field === "adv_chart_status"
    ? [
        "In-progress",
        "Practice Clarification",
        "Outreach Needed",
        "Walk-In",
        "Completed",
        "N/A",
      ]
    : ["In-progress", "Practice Clarification", "Completed", "N/A"];
};

export const handleExportExcel = ({
  data,
  setSnackbar,
  practiceUsers = [],
  dateOfService,
  dos,
}) => {
  if (!data?.length) {
    setSnackbar({
      open: true,
      message: "No data available to export",
      severity: "warning",
    });
    return;
  }

  const formatDate = (date) => {
    if (!date) return "";
    return dayjs(date).isValid() ? getFormattedDate(date) : "";
  };

  const formatDateTime = (date) => {
    if (!date) return "";
    return dayjs(date).isValid() ? getFormattedDateTime(date) : "";
  };

  const getUserNameById = (userId) => {
    const user = practiceUsers.find((u) => u.user_id === userId);
    return user?.user_name || user?.email || userId || "";
  };

  const parseErrorDetails = (errorDetails) => {
    if (!errorDetails) return {};

    try {
      return typeof errorDetails === "string"
        ? JSON.parse(errorDetails)
        : errorDetails;
    } catch (e) {
      console.log("e: ", e);
      return {};
    }
  };

  const joinList = (value) => {
    if (!Array.isArray(value)) return "";
    return value.join(", ");
  };

  const exportData = data.map((row) => {
    const parsedError = parseErrorDetails(row.error_details);

    const yesData = parsedError?.yes_data || {};
    const reasonsByLocation = yesData?.reasons_by_location || {};
    const notesByLocation = yesData?.notes_by_location || {};
    const history = Array.isArray(parsedError?.history)
      ? parsedError.history
      : [];
    const lastHistory = history.length ? history[history.length - 1] : null;

    return {
      "Encounter ID": row.encounter_id || "",
      DOS: formatDate(row.dos),
      "DOS Time": row.dos_time || "",
      "Patient Name": `${row.first_name || ""} ${row.last_name || ""}`.trim(),
      "Provider Name":
        `${row.provider_first_name || ""} ${row.provider_last_name || ""}`.trim(),
      "Appt Type": row.appt_type || "",

      "Advanced Chart Status": row.adv_chart_status || "",
      "Advanced Chart Agent": getUserNameById(row.resource_id_adv_chart),
      "Advanced Chart Completion Date": formatDate(row.adv_chart_compl_date),

      "Dictation ID": row.dictation_id || "",
      "Dictation Created Date": formatDateTime(row.dictation_created_date),

      "Scribing Status": row.scribing_status || "",
      "Scribing Agent": getUserNameById(row.resource_id_scribing),
      "Scribing Completion Date": formatDate(row.scribing_compl_date),

      "Coding Status": row.coding_status || "",
      "Coding Agent": getUserNameById(row.resource_id_coding),
      "Coding Completion Date": formatDate(row.coding_compl_date),

      "Error Exists": row.error_exists || "",
      "Current Error Status": parsedError.current_status || "",
      "No Error Reason": parsedError.no_error_reason || "",
      "Additional Notes": parsedError.additional_notes || "",
      "Error Locations": joinList(yesData.error_locations),

      "Advanced Charting Reasons": joinList(
        reasonsByLocation["Advanced Charting"],
      ),
      "Advanced Charting Error Notes":
        notesByLocation["Advanced Charting"] || "",

      "Scribing Reasons": joinList(reasonsByLocation["Scribing"]),
      "Scribing Error Notes": notesByLocation["Scribing"] || "",

      "Coding Reasons": joinList(reasonsByLocation["Coding"]),
      "Coding Error Notes": notesByLocation["Coding"] || "",

      "Last Error Updated By": lastHistory?.created_by || "",
      "Last Error Updated At": formatDateTime(lastHistory?.created_at),

      Notes: row.tracker_notes || "",
      "Tracker ID": row.tracker_id || "",
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, "Dictation Summary");

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  const fileData = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
  });

  const fileName = `dictation_summary_${
    dateOfService || dayjs(dos).format("MM_DD_YYYY")
  }.xlsx`;

  saveAs(fileData, fileName);
};

export const columns = (
  updateTrackerStatus,
  updateSelectedDictations,
  updateErrorFields,
  updateAssignedUser,
  practiceUsers,
) => {
  return [
    {
      field: "status_icon",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const { adv_chart_status, coding_status, scribing_status } = params.row;

        const allCompleted =
          adv_chart_status === "Completed" &&
          coding_status === "Completed" &&
          scribing_status === "Completed";

        return allCompleted ? (
          <CheckCircleIcon style={{ color: "#4caf50" }} />
        ) : (
          <WarningAmberIcon style={{ color: "#ff9800" }} />
        );
      },
    },

    { field: "encounter_id", headerName: "ID", width: 110 },
    {
      field: "dos",
      headerName: "DOS",
      width: 180,

      valueGetter: (value, row) => {
        if (!value) return 0;

        const d = new Date(value);

        if (row?.dos_time) {
          const [time, modifier] = row.dos_time.split(" ");
          let [hours, minutes] = time.split(":").map(Number);

          if (modifier === "PM" && hours !== 12) hours += 12;
          if (modifier === "AM" && hours === 12) hours = 0;

          d.setHours(hours, minutes, 0, 0);
        } else {
          d.setHours(0, 0, 0, 0);
        }

        return d.getTime();
      },

      renderCell: (params) => {
        return `${params.row.dos}, ${params.row.dos_time || ""}`;
      },
    },
    {
      field: "first_name",
      headerName: "PT Name",
      width: 180,
      renderCell: (params) => (
        <div>{`${params.value} ${params.row.last_name}`}</div>
      ),
    },
    {
      field: "provider_first_name",
      headerName: "Provider Name",
      width: 130,
      renderCell: (params) => (
        <div>{`${params.value} ${params.row.provider_last_name}`}</div>
      ),
    },
    {
      field: "appt_type",
      headerName: "Appt Type",
      width: 130,
    },

    {
      field: "adv_chart_status",
      headerName: "Adv Chart Status",
      width: 200,
      renderCell: (params) => (
        <StatusCell
          id={params.row.patient_id}
          field="adv_chart_status"
          value={params.value}
          row={params.row}
          apiRef={null}
          updateTrackerStatus={updateTrackerStatus}
          completionDateField="adv_chart_compl_date"
        />
      ),
    },
    {
      field: "resource_id_adv_chart",
      headerName: "Adv Chart Agent",
      width: 220,
      sortable: false,
      renderCell: (params) => (
        <AssignUserCell
          id={params.row.patient_id}
          value={params.value}
          row={params.row}
          field="resource_id_adv_chart"
          users={practiceUsers}
          updateAssignedUser={updateAssignedUser}
          labelPrefix="Adv Chart"
        />
      ),
    },
    {
      field: "dictation_id",
      headerName: "DictationID",
      width: 130,
    },
    {
      field: "dictation_created_date",
      headerName: "Dictation Created Date",
      width: 180,
      valueFormatter: dateFormatter,
      valueGetter: getFormattedDate,
    },
    {
      field: "scribing_status",
      headerName: "Scribing Status",
      width: 200,
      renderCell: (params) => (
        <StatusCell
          id={params.row.patient_id}
          field="scribing_status"
          value={params.value}
          row={params.row}
          apiRef={null}
          updateTrackerStatus={updateTrackerStatus}
          completionDateField="scribing_compl_date"
        />
      ),
    },
    {
      field: "resource_id_scribing",
      headerName: "Scribing Agent",
      width: 220,
      sortable: false,
      renderCell: (params) => (
        <AssignUserCell
          id={params.row.patient_id}
          value={params.value}
          row={params.row}
          field="resource_id_scribing"
          users={practiceUsers}
          updateAssignedUser={updateAssignedUser}
          labelPrefix="Scribing"
        />
      ),
    },
    {
      field: "coding_status",
      headerName: "Coding Status",
      width: 200,
      renderCell: (params) => (
        <StatusCell
          id={params.row.patient_id}
          field="coding_status"
          value={params.value}
          row={params.row}
          apiRef={null}
          updateTrackerStatus={updateTrackerStatus}
          completionDateField="coding_compl_date"
        />
      ),
    },

    {
      field: "resource_id_coding",
      headerName: "Coding Agent",
      width: 220,
      sortable: false,
      renderCell: (params) => (
        <AssignUserCell
          id={params.row.patient_id}
          value={params.value}
          row={params.row}
          field="resource_id_coding"
          users={practiceUsers}
          updateAssignedUser={updateAssignedUser}
          labelPrefix="Coding"
        />
      ),
    },
    {
      field: "error_exists",
      headerName: "Error Exists",
      minWidth: 180,
      sortable: false,
      renderCell: (params) => (
        <ErrorCell row={params.row} onSave={updateErrorFields} />
      ),
    },
    {
      field: "error_details_preview",
      headerName: "Error Notes",
      minWidth: 260,
      sortable: false,
      renderCell: (params) => {
        const details =
          typeof params.row.error_details === "string"
            ? (() => {
                try {
                  return JSON.parse(params.row.error_details);
                } catch {
                  return null;
                }
              })()
            : params.row.error_details;

        const history = Array.isArray(details?.history) ? details.history : [];

        return (
          <ErrorDetailsPreview
            row={params.row}
            details={details}
            history={history}
          />
        );
      },
    },

    {
      field: "tracker_notes",
      headerName: "Notes",
      width: 250,
      renderCell: (params) => (
        <NotesCell
          row={params.row}
          field="tracker_notes"
          updateTrackerStatus={updateTrackerStatus}
        />
      ),
    },

    {
      field: "tracker_id",
      headerName: "Tracker Id",
      width: 100,
    },
  ];
};

export const ERROR_LOCATIONS = ["Advanced Charting", "Scribing", "Coding"];

export const NO_ERROR_OPTIONS = [
  "No Errors Found",
  "Errors Found but already fixed",
];

export const ERROR_REASON_OPTIONS = {
  "Advanced Charting": [
    "Incorrect Medical Notes: History of Present Illness (HPI)",
    "Incorrect Medical Notes: Chief Complaint",
    "Incorrect Medical Notes: Patient Care Team",
    "Incorrect Medical Notes: Medical History",
    "Incorrect Medical Notes: Allergies",
    "Incorrect Medical Notes: Gyn/OB",
    "Incorrect Medical Notes: Surgical History",
    "Incorrect Medical Notes: Hospitalization",
    "Incorrect Medical Notes: Family History",
    "Incorrect Medical Notes: Social History",
    "Incorrect Medical Notes: Review of System (ROS)",
    "Incorrect Medical Notes: Physcial Examination",
    "Incorrect Medical Notes: Assessment",
    "Incorrect Medical Notes: Treatment Plan",
    "Incorrect Medical Notes: Medication",
    "Incorrect AdvCharting Notes: Summary of Las Visit",
    "Incorrect AdvCharting Notes: Preventive Care",
    "Incorrect AdvCharting Notes: Chronic Conditions",
    "Incorrect AdvCharting Notes: Labatory",
    "Incorrect AdvCharting Notes: Diagnoistic Tests",
    "Incorrect AdvCharting Notes: Mental Health",
    "Incorrect AdvCharting Notes: Vaccination",
  ],
  Scribing: [
    "Incorrect Medical Notes: History of Present Illness (HPI)",
    "Incorrect Medical Notes: Chief Complaint",
    "Incorrect Medical Notes: Patient Care Team",
    "Incorrect Medical Notes: Medical History",
    "Incorrect Medical Notes: Allergies",
    "Incorrect Medical Notes: Gyn/OB",
    "Incorrect Medical Notes: Surgical History",
    "Incorrect Medical Notes: Hospitalization",
    "Incorrect Medical Notes: Family History",
    "Incorrect Medical Notes: Social History",
    "Incorrect Medical Notes: Review of System (ROS)",
    "Incorrect Medical Notes: Physcial Examination",
    "Incorrect Medical Notes: Assessment",
    "Incorrect Medical Notes: Treatment Plan",
    "Incorrect Medical Notes: Medication",
  ],
  Coding: [
    "Coding Issues: History of Present Illness (HPI)",
    "Coding Issues: Chief Complaint",
    "Coding Issues: Medical History",
    "Coding Issues: Notes: Allergies",
    "Coding Issues: Gyn/OB",
    "Coding Issues: Surgical History",
    "Coding Issues: Hospitalization",
    "Coding Issues: Family History",
    "Coding Issues: Social History",
    "Coding Issues: Review of System (ROS)",
    "Coding Issues: Physcial Examination",
    "Coding Issues: Assessment",
    "Coding Issues: Treatment Plan",
    "Coding Issues: Medication",
    "Coding Issues: Missing SNOWMED",
  ],
};
